const ACTIVE_SUBSCRIPTION_STATUSES = Object.freeze(['active', 'trialing', 'past_due']);

function isAdminRole(user) {
  return user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
}

function isProTier(user) {
  return user?.subscription === 'PRO' || user?.subscription === 'PRO_PLUS';
}

function hasActivePaidAccess(user) {
  if (!user) return false;
  if (isAdminRole(user)) return true;
  const passUntil = user.proDayPassExpiresAt;
  if (passUntil && new Date(passUntil).getTime() > Date.now()) {
    return true;
  }
  if (!isProTier(user)) return false;

  if (user.subscriptionEndsAt) {
    return new Date(user.subscriptionEndsAt) > new Date();
  }

  return (
    !user.subscriptionStatus ||
    ACTIVE_SUBSCRIPTION_STATUSES.includes(user.subscriptionStatus)
  );
}

/**
 * True when FREE_FIRST public config on the user session unlocks Pro feature surfaces.
 * Not a paid entitlement - Checkout/IAP remain separately gated.
 */
function hasLaunchProSurfaceAccess(user) {
  return user?.freeFirst?.unlockProSurfaces === true;
}

/**
 * Hide Pro upgrade / "included with Pro" marketing.
 * Fail closed: hide unless unlockProSurfaces is explicitly false.
 * Stale localStorage users often lack freeFirst during launch.
 */
function shouldHideProStatusChrome(user) {
  return user?.freeFirst?.unlockProSurfaces !== false;
}

/**
 * Feature access for UI / requirePro-equivalent client gates.
 * Prefer this over hasActivePaidAccess when deciding if Bonk Lab etc. should open.
 */
function hasProFeatureAccess(user) {
  return hasActivePaidAccess(user) || hasLaunchProSurfaceAccess(user);
}

function hasFeatureFlag(user, flagKey) {
  if (!user || !flagKey) return false;
  return Boolean(user?.preferences?.featureFlags?.[flagKey]);
}

function isTrainerEligible(user) {
  // Product door must be open when the session carries productDoors.
  if (user?.productDoors && user.productDoors.trainer !== true) return false;
  // Pro subscribers always have Trainer Mode. The trainerBetaAccess flag
  // additionally grants access to non-Pro staff / beta testers.
  return hasProFeatureAccess(user) || hasFeatureFlag(user, 'trainerBetaAccess');
}

export {
  ACTIVE_SUBSCRIPTION_STATUSES,
  isAdminRole,
  isProTier,
  hasActivePaidAccess,
  hasLaunchProSurfaceAccess,
  shouldHideProStatusChrome,
  hasProFeatureAccess,
  hasFeatureFlag,
  isTrainerEligible,
};
